import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { PlayerInfo } from "../game/types";
import { skinFill, glowShadow, ringStyle } from "../game/cosmetics";
import { FX_BUMP_MS, FX_CLASS_CRUMB, FX_CLASS_FLOAT, FX_CLASS_RIPPLE, fxIsOff } from "../game/fxTiming";
import { useBakeryStore } from "../game/bakeryStore";
import { usePrefs } from "../contexts/PrefsContext";
import { useMusic } from "../contexts/MusicContext";
import { bakeryStatus, fmtAgo, fmtCompact, fmtInt, specLabel, toNum } from "../game/format";
import { avatarCdnUrl } from "@cookies/shared/cdn";
import { AVATAR_TIMEOUT_MS, AVATAR_TIMEOUT_SMALL_MS, FX_BUMP_THRESHOLDS, IDLE_SHORT_MS, IDLE_LONG_MS, CLICK_BOUNDS_MIN, CLICK_BOUNDS_MAX } from "@cookies/shared/config";

function HandAvatar({ pl }: { pl: PlayerInfo }) {
  const url = avatarCdnUrl(pl.userId, pl.avatar ?? "", 64);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ring = ringStyle(pl.cosmetics?.ring);
  useEffect(() => {
    if (!url || failed || loaded) return;
    const t = window.setTimeout(() => setFailed(true), AVATAR_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [url, failed, loaded]);
  if (url && !failed) {
    return (
      <span className="bk-handavatar" style={{ ["--color" as string]: pl.color, borderColor: pl.color, padding: 0, overflow: "hidden", ...ring } as CSSProperties}>
        <img src={url} alt={pl.name} width={64} height={64} crossOrigin="anonymous" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} />
      </span>
    );
  }
  return <span className="bk-handavatar" style={{ ["--color" as string]: pl.color, borderColor: pl.color, ...ring } as CSSProperties}>{pl.name.slice(0, 1)}</span>;
}

function CloudAvatar({ pl }: { pl: PlayerInfo }) {
  const url = avatarCdnUrl(pl.userId, pl.avatar ?? "", 64);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ring = ringStyle(pl.cosmetics?.ring);
  useEffect(() => {
    if (!url || failed || loaded) return;
    const t = window.setTimeout(() => setFailed(true), AVATAR_TIMEOUT_SMALL_MS);
    return () => window.clearTimeout(t);
  }, [url, failed, loaded]);
  if (url && !failed) {
    return (
      <span className="bk-cloud-ava" style={{ background: pl.color, padding: 0, overflow: "hidden", ...ring } as CSSProperties}>
        <img src={url} alt={pl.name} width={32} height={32} crossOrigin="anonymous" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} />
      </span>
    );
  }
  return <span className="bk-cloud-ava" style={{ background: pl.color, ...ring } as CSSProperties}>{pl.name.slice(0, 1)}</span>;
}

function IconHand({ fill }: { fill: string }) {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" style={{ shapeRendering: "crispEdges" as const }}>
      <g stroke="#3a2115" strokeWidth="1.2" strokeLinejoin="round">
        <rect x="6" y="4" width="3" height="7" rx="0.9" fill={fill} />
        <rect x="9.6" y="2.6" width="3" height="8.2" rx="0.9" fill={fill} />
        <rect x="13.2" y="4" width="3" height="7" rx="0.9" fill={fill} />
        <rect x="16.6" y="6" width="2.6" height="5" rx="0.8" fill={fill} />
        <rect x="6" y="10.2" width="13" height="7.4" rx="2" fill={fill} />
        <rect x="4.4" y="11.6" width="2.4" height="4.2" rx="0.8" fill={fill} />
      </g>
      <rect x="8" y="13.6" width="7" height="1" rx="0.4" fill="#3a2115" opacity="0.18" />
    </svg>
  );
}

function IconWrench() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path d="M16.2 2.6 A3.7 3.7 0 0 1 18.6 9 L12.6 15 L10.4 12.8 L16.4 6.8 A3.7 3.7 0 0 1 16.2 2.6 Z" fill="#c7d2d8" stroke="#3a2115" strokeWidth="1.3" strokeLinejoin="round" />
      <rect x="4.2" y="16" width="8.2" height="3.6" rx="1" fill="#c7d2d8" stroke="#3a2115" strokeWidth="1.3" transform="rotate(45 8.3 17.8)" />
    </svg>
  );
}

function IconMegaphone() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path d="M4 9 H8.2 L14 4.6 V19.4 L8.2 15 H4 Z" fill="#ffdf9e" stroke="#3a2115" strokeWidth="1.25" strokeLinejoin="round" />
      <rect x="14" y="8" width="6.2" height="8" rx="1" fill="#fff3d0" stroke="#3a2115" strokeWidth="1.2" />
      <rect x="15.2" y="10" width="3.8" height="4" rx="0.6" fill="#ffd23f" stroke="#3a2115" strokeWidth="0.9" />
      <circle cx="17.1" cy="12" r="0.9" fill="#3a2115" />
    </svg>
  );
}

function IconMixer() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <rect x="5" y="4" width="14" height="3.2" rx="1" fill="#a8b0b8" stroke="#3a2115" strokeWidth="1.2" />
      <rect x="7" y="7.2" width="10" height="5.4" rx="1.2" fill="#fff3d0" stroke="#3a2115" strokeWidth="1.2" />
      <rect x="9.5" y="12.6" width="5" height="6.2" rx="1" fill="#c7d2d8" stroke="#3a2115" strokeWidth="1.2" />
      <rect x="10.5" y="9" width="3" height="2" rx="0.6" fill="#ffd23f" stroke="#3a2115" strokeWidth="0.9" />
    </svg>
  );
}

function ToolIcon({ spec, autoLevel }: { spec: string; autoLevel: number }) {
  const lb = specLabel(spec)
  if (lb === "Engineer") return <IconWrench />;
  if (lb === "Coordinator") return <IconMegaphone />;
  if (lb === "Automation") return <IconHand fill={autoLevel >= 5 ? "#c7d2d8" : "#9aa8b3"} />;
  if (lb === "Fortune") return <IconHand fill="#ffd23f" />;
  if (autoLevel >= 1) return <IconHand fill="#a8b0b8" />;
  return <IconHand fill="#e9c29a" />;
}

export interface Floater { id: number; x: number; y: number; text: string; color: string; big: boolean }
export interface Crumb { id: number; x: number; y: number; dx: number; dy: number; s: number }
export interface Ripple { id: number; x: number; y: number }
export interface BakeSceneContextValue {
  press: boolean;
  ripples: Ripple[];
  crumbs: Crumb[];
  floaters: Floater[];
  lunging: string[];
  lungeTargets?: Record<string, { x: number; y: number }>;
  onTap: (x: number, y: number) => void;
  onSneakyClick?: (id: string, x: number, y: number) => void;
  onMiniClick?: (id: string, x: number, y: number) => void;
}
export const BakeSceneContext = createContext<BakeSceneContextValue | null>(null);
export function useBakeSceneContext(): BakeSceneContextValue {
  const v = useContext(BakeSceneContext);
  if (!v) throw new Error("BakeScene must be within BakeSceneContext.Provider");
  return v;
}
function normalizeTs(v: number): number { if (!Number.isFinite(v) || v <= 0) return 0; if (v > 1e12) return v; if (v > 1e10) return v; return v * 1000; }
function lastSeenMs(pl: PlayerInfo): number { const rec = pl as unknown as Record<string, unknown>; const maybeLastSeen = rec["lastSeen"]; if (typeof maybeLastSeen === "number" && maybeLastSeen > 0) return normalizeTs(maybeLastSeen); const maybeHeartbeat = rec["heartbeatAt"]; if (typeof maybeHeartbeat === "number" && maybeHeartbeat > 0) return normalizeTs(maybeHeartbeat); return normalizeTs(pl.lastClickAt ?? 0); }
function crowdMode(n: number): string { if (n <= 3) return "large"; if (n <= 10) return "normal"; if (n <= 30) return "dense"; return "dots"; }
const CHIPS: Array<[number, number, number, number]> = [ [90, 48, 11, -18], [118, 48, 9, 22], [148, 82, 12, -12], [135, 122, 10, 35], [92, 148, 13, -25], [58, 118, 9, 15], [48, 85, 11, -30], [72, 72, 14, 8], [112, 98, 8, -8], [102, 70, 10, 28], ];
export default function BakeScene() {
  const ctx = useContext(BakeSceneContext);
  if (ctx === null) throw new Error("BakeScene must be within BakeSceneContext.Provider");
  const p: BakeSceneContextValue = ctx;
  const store = useBakeryStore();
  const prefs = usePrefs();
  useMusic();
  const snap = store.snapshot;
  const userId = store.userId;
  const density = prefs.density;
  const reduced = prefs.reduced;
  const shown = store.shownCookies;
  const credits = store.liveCredits;
  const cps = snap ? toNum(snap.bakery.cps) : 0;
  void cps;
  const cookiesPerClick = store.cookiesPerClick;
  const creditsPerClick = store.creditsPerClick;
  const clickBreakdown = store.clickBreakdown;
  const [now, setNow] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const players: PlayerInfo[] = useMemo(() => snap?.players ?? [], [snap]);
  const active = useMemo(() => players.filter((pl) => pl.active), [players]);
  const rushOn = useMemo(() => {
    const ev = snap?.events?.find((e) => e.id === "cookie_rush") ?? null;
    return !!ev && ev.endsAt > now;
  }, [snap, now]);
  const goldenActive = useMemo(() => {
    const ev = snap?.events?.find((e) => e.id === "golden_cookie") ?? null;
    return !!ev && ev.endsAt > now;
  }, [snap, now]);
  const st = bakeryStatus(active.length, rushOn, shown);
  const chaos = active.length > 15 || st.cls === "chaos";
  const quiet = st.cls === "quiet";
  const mode = crowdMode(Math.max(active.length, 1));
  const selfLoadout = snap?.me.cosmetics?.equipped;
  const skin = selfLoadout?.skin ?? "";
  const trail = selfLoadout?.trail ?? "";
  const autoLevel = useMemo(() => {
    const mePl = players.find((pl) => pl.userId === userId);
    if (mePl !== undefined && typeof mePl.autoLevel === "number") return mePl.autoLevel;
    const u = snap?.me?.upgrades;
    if (u) return (u["oven_assistant"] ?? 0) + (u["auto_kneader"] ?? 0) + (u["cookie_sorter"] ?? 0) + (u["delivery_assistant"] ?? 0);
    const arr = snap?.upgrades;
    if (!arr) return 0;
    let s = 0;
    for (const x of arr) if (x.id === "oven_assistant" || x.id === "auto_kneader" || x.id === "cookie_sorter" || x.id === "delivery_assistant") s += x.level;
    return s;
  }, [snap, players, userId]);
  const visibleHands = useMemo(() => {
    const list = [...active].sort((a, b) => (a.userId === userId ? -1 : b.userId === userId ? 1 : 0));
    if (mode === "dots") return list.slice(0, 12);
    if (mode === "dense") return list.slice(0, 18);
    return list.slice(0, 10);
  }, [active, mode, userId]);
  const dotHands = useMemo(() => {
    if (mode === "dots") return active.slice(12, 30);
    if (mode === "dense" && active.length > 18) return active.slice(18, 30);
    return [];
  }, [active, mode]);
  const withSelfCos = useCallback((pl: PlayerInfo): PlayerInfo => {
    const loadout = selfLoadout;
    return pl.userId === userId && loadout ? { ...pl, cosmetics: { ...loadout } } : pl;
  }, [selfLoadout, userId]);
  // handsForScene gates lunging to actual clicks only (p.lunging + lungeTargets), not idle auto display.
  const handsForScene = useMemo(() => visibleHands.map((pl) => {
    const base = withSelfCos(pl);
    const lv = typeof pl.autoLevel === "number" ? pl.autoLevel : pl.userId === userId ? autoLevel : 0;
    const isLunging = p.lunging.includes(pl.userId) && p.lungeTargets?.[pl.userId] !== undefined;
    return { ...base, autoLevel: lv, cps: pl.cps ?? "0", isLunging };
  }), [visibleHands, autoLevel, withSelfCos, userId, p.lunging, p.lungeTargets]);
  const hands = handsForScene;
  const dots = useMemo(() => dotHands.map((pl) => ({
    ...pl,
    autoLevel: typeof pl.autoLevel === "number" ? pl.autoLevel : 0,
    cps: pl.cps ?? "0",
  })), [dotHands]);
  const hiddenCount = Math.max(0, active.length - visibleHands.length - dotHands.length);
  const idleBakers = useMemo(() => players.filter((pl) => !pl.active), [players]);
  const sneakyRaw = snap?.events?.find((e) => e.id === "sneaky_cookie") ?? null;
  const sneaky = sneakyRaw && sneakyRaw.endsAt > now ? sneakyRaw : null;
  const sneakyState = sneaky ? { id: sneaky.id, x: sneaky.x ?? 0.5, y: sneaky.y ?? 0.5, sneakyId: sneaky.sneakyId, endsAt: sneaky.endsAt } : null;
  const minis = (snap?.events ?? []).filter((e) => e.id === "mini_cookie" && e.endsAt > now && e.cookieId).map((e) => ({ cookieId: e.cookieId as string, x: e.x ?? 0.5, y: e.y ?? 0.5, multiplier: e.multiplier ?? 2, endsAt: e.endsAt }));
  const [bump, setBump] = useState(false);
  const prevCredits = useRef(credits);
  const [autoPulse, setAutoPulse] = useState<Set<string>>(new Set());
  const [autoRipples, setAutoRipples] = useState<Ripple[]>([]);
  const [cloudOpen, setCloudOpen] = useState(false);
  const fallbackAutoLevel = autoLevel ?? 0;
  const mediaReduced = (() => { try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; } })();
  const reducedEffective = reduced ?? mediaReduced;
  useEffect(() => {
    const prev = prevCredits.current;
    const curr = credits;
    if (curr > prev) {
      let crossed = false;
      for (const m of FX_BUMP_THRESHOLDS) if (prev < m && curr >= m) crossed = true;
      if (Math.floor(curr / 10000) > Math.floor(prev / 10000)) crossed = true;
      if (crossed) {
        setBump(true);
        const t = window.setTimeout(() => setBump(false), FX_BUMP_MS);
        prevCredits.current = curr;
        return () => window.clearTimeout(t);
      }
    }
    prevCredits.current = curr;
  }, [credits]);
  // Gate idle: ripples/crumbs only onTap, not passive. Previously autoPulse/autoRipples ran continuously
  // for any active hand with autoLevel, causing circles even when idle. Now disabled so idle shows no ripple.
  useEffect(() => {
    // Only lunge/ripple when actually clicking (p.lunging). Clear any auto timers.
    setAutoPulse(new Set());
    setAutoRipples([]);
    if (reducedEffective || fxIsOff(density)) return;
    // No passive auto timers — idle must show no lunge/ripple. Actual clicks use p.lunging/p.ripples/crumbs.
    return;
  }, [reducedEffective, density]);
  const tapFrom = (clientX: number, clientY: number, el: HTMLElement | null) => {
    if (!el) { p.onTap(0.5, 0.5); return; }
    const r = el.getBoundingClientRect();
    const x = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, (clientX - r.left) / r.width));
    const y = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, (clientY - r.top) / r.height));
    p.onTap(x, y);
  };
  const rawIdleSource: PlayerInfo[] = idleBakers;
  const idleList: PlayerInfo[] = (() => {
    const out: PlayerInfo[] = [];
    for (const pl of rawIdleSource) {
      if (pl.active) continue;
      const contrib = toNum(pl.contribution ?? "0");
      const last = lastSeenMs(pl);
      const age = last > 0 ? now - last : Number.POSITIVE_INFINITY;
      if (last > 0 && age < IDLE_SHORT_MS) continue;
      if (last > 0 && age < IDLE_LONG_MS) { out.push(pl); continue; }
      if (contrib > 0) { out.push(pl); continue; }
    }
    return out;
  })();
  const idleCount = idleList.length;
  const sortedIdle = [...idleList].sort((a, b) => {
    const la = lastSeenMs(a);
    const lb = lastSeenMs(b);
    if (la !== lb) return lb - la;
    return toNum(b.contribution ?? "0") - toNum(a.contribution ?? "0");
  });
  const fill = skinFill(skin);
  const trailCls = trail ? " trail-" + trail.replace(/[^a-z_]/g, "") : "";
  return (
    <div className={"bk-scene" + (rushOn ? " rush" : "") + (goldenActive ? " golden" : "") + (chaos ? " chaos" : "")}>
      <div className="bk-wall" />
      <div className="bk-beams" />
      <div className="bk-oven">
        <div className="bk-oven-mouth">
          <div className="bk-fire"><i /><i /><i /><i /><i /><i /><i /></div>
        </div>
      </div>
      <div className="bk-shelf left"><i><span className="bk-shelf-icon"><IconMixer /></span></i><i style={{ fontStyle: "normal", fontSize: 22 }}>🍯</i><i style={{ fontStyle: "normal", fontSize: 22 }}>🥛</i></div>
      <div className="bk-shelf right"><i><span className="bk-shelf-icon"><IconMixer /></span></i><i style={{ fontStyle: "normal", fontSize: 22 }}>🫖</i><i style={{ fontStyle: "normal", fontSize: 22 }}>🍞</i></div>
      <div className="bk-lamps">
        <span className="bk-lamp"><span className="bk-lamp-bulb" /><span className="bk-lamp-glow" /></span>
        <span className="bk-lamp"><span className="bk-lamp-bulb" /><span className="bk-lamp-glow" /></span>
        <span className="bk-lamp"><span className="bk-lamp-bulb" /><span className="bk-lamp-glow" /></span>
      </div>
      <div className="bk-floor" />
      <div className="bk-warm" />
      <div className="bk-rushaura" />
      <div className="bk-sacks">🌾🧺</div>
      <div className="bk-sacks right">🧺🌾</div>
      <div className="bk-dust"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>
      <div className="bk-embers"><i /><i /><i /><i /><i /><i /></div>
      <div className={"bk-calm-msg" + (quiet ? " visible" : "")} aria-hidden={!quiet}>The bakery is quiet. Start baking to wake it up!</div>
      {hands.map((pl, i) => {
        const n = hands.length;
        const span = (3 * Math.PI) / 2;
        const base = -Math.PI / 2;
        const ang = n <= 1 ? base : base - span / 2 + (i / (n - 1)) * span;
        const rx = mode === "large" ? 32 : mode === "normal" ? 38 : 43;
        const ry = mode === "large" ? 30 : mode === "normal" ? 36 : 40;
        const lx = 50 + Math.cos(ang) * rx;
        const ly = 44 + Math.sin(ang) * ry;
        const isUser = pl.userId === userId;
        const lv = typeof pl.autoLevel === "number" ? pl.autoLevel : isUser ? fallbackAutoLevel : 0;
        const evo = lv >= 5 ? " auto-lv5" : lv >= 3 ? " auto-lv3" : lv >= 1 ? " auto-lv1" : "";
        // Gate lunging/auto so idle shows no lunge/ripple: only when actually in lunging set with a target
        const pulsing = autoPulse.has(pl.userId) && p.lunging.includes(pl.userId);
        const autoCls = pulsing && lv >= 1 ? " auto" : "";
        const isLunging = p.lunging.includes(pl.userId) && p.lungeTargets?.[pl.userId] !== undefined;
        const target = p.lungeTargets?.[pl.userId];
        let lungeStyle: CSSProperties = { left: lx + "%", top: ly + "%" };
        if (isLunging && target !== undefined) {
          const vx = (target.x * 100 - lx) / 100;
          const vy = (target.y * 100 - ly) / 100;
          const len = Math.hypot(vx, vy) || 1;
          const step = Math.min(14, Math.max(8, len * 30));
          const dx = (vx / len) * step;
          const dy = (vy / len) * step;
          lungeStyle = { left: lx + "%", top: ly + "%", ["--lx" as string]: dx.toFixed(1) + "px", ["--ly" as string]: dy.toFixed(1) + "px" } as CSSProperties;
        }
        return (
          <div
            key={pl.userId}
            className={"bk-handwrap " + mode + (isLunging ? " lunging" : "") + autoCls + (lv >= 1 ? " has-auto" : "") + evo + (isUser ? " is-self" : "") + (isUser && rushOn ? " rushing" : "")}
            style={lungeStyle}
          >
            <HandAvatar pl={pl} />
            <span className={"bk-handtool" + evo}>
              <ToolIcon spec={pl.spec} autoLevel={lv} />
              {lv >= 3 && <span className="bk-tool-sparkle" aria-hidden="true"><svg viewBox="0 0 10 10" width="10" height="10"><path d="M5 0 L6 3.8 L10 5 L6 6.2 L5 10 L4 6.2 L0 5 L4 3.8 Z" fill="#ffd23f" stroke="#3a2115" strokeWidth="0.7" /></svg></span>}
              {lv >= 3 && <span className="bk-tool-steam" aria-hidden="true"><i /><i /><i /></span>}
              {lv >= 5 && <span className="bk-tool-mech" aria-hidden="true" />}
            </span>
            {(mode === "large" || mode === "normal") && <span className="bk-handname" style={glowShadow(pl.cosmetics?.glow) ? { textShadow: glowShadow(pl.cosmetics?.glow) } : undefined}>{pl.name}</span>}
          </div>
        );
      })}
      {dots.map((d, i) => (
        <span
          key={d.userId}
          className="bk-crowddot"
          style={{ background: d.color, left: (8 + ((i * 37) % 84)) + "%", top: (64 + ((i * 23) % 24)) + "%", animationDelay: ((i % 6) * -0.32) + "s", animationDuration: (1.6 + (i % 4) * 0.28) + "s" } as CSSProperties}
        />
      ))}
      <button
        type="button"
        className={"bk-cookie" + (p.press ? " press" : "") + (rushOn || goldenActive ? " glow" : "")}
          onPointerDown={(e) => {
          if (!e.isPrimary) return;
          if (e.pointerType === "mouse" && e.button !== 0) return;
          const wrap = e.currentTarget.parentElement as HTMLElement | null;
          if (!wrap) { p.onTap(0.5, 0.5); return; }
          const r = wrap.getBoundingClientRect();
          const x = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, (e.clientX - r.left) / r.width));
          const y = Math.min(CLICK_BOUNDS_MAX, Math.max(CLICK_BOUNDS_MIN, (e.clientY - r.top) / r.height));
          p.onTap(x, y);
        }}
        aria-label="Bake the giant cookie"
      >
        <svg viewBox="0 0 200 200" role="img" aria-hidden="true">
          <defs>
            <radialGradient id="crustBase" cx="42%" cy="36%" r="72%">
              <stop offset="0%" stopColor={fill.base[0]} />
              <stop offset="40%" stopColor={fill.base[1]} />
              <stop offset="74%" stopColor={fill.base[2]} />
              <stop offset="100%" stopColor={fill.base[3]} />
            </radialGradient>
            <radialGradient id="crustInner" cx="50%" cy="48%" r="58%">
              <stop offset="0%" stopColor={fill.inner[0]} stopOpacity="0.96" />
              <stop offset="62%" stopColor={fill.inner[1]} stopOpacity="0.45" />
              <stop offset="100%" stopColor={fill.inner[2]} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="chipGrad" cx="35%" cy="30%" r="78%">
              <stop offset="0%" stopColor={fill.chipHi} />
              <stop offset="58%" stopColor={fill.chip} />
              <stop offset="100%" stopColor={fill.chip} />
            </radialGradient>
          </defs>
          <path
            d="M100 8 C136 10 172 34 180 78 C188 122 168 164 124 182 C86 198 38 188 16 152 C-4 118 8 70 36 38 C56 16 72 6 100 8 Z"
            fill="url(#crustBase)"
            stroke="#3a2115"
            strokeWidth="9"
            strokeLinejoin="round"
          />
          <path
            d="M100 28 C128 30 156 48 162 80 C168 114 152 144 120 156 C92 166 56 158 40 132 C26 108 30 74 52 52 C66 38 80 26 100 28 Z"
            fill="url(#crustInner)"
            opacity="0.95"
          />
          <ellipse cx="72" cy="58" rx="26" ry="15" fill="#ffdf9e" opacity="0.42" transform="rotate(-18 72 58)" />
          {CHIPS.map((c, i) => (
            <g key={i} transform={`translate(${c[0]} ${c[1]})`}>
              <ellipse rx={c[2]} ry={c[2] * 0.88} fill="url(#chipGrad)" stroke="#5a3421" strokeWidth="1.8" transform={`rotate(${c[3]})`} />
              <ellipse cx={-c[2] * 0.22} cy={-c[2] * 0.28} rx={c[2] * 0.26} ry={c[2] * 0.15} fill="#7a4a2b" opacity="0.28" />
            </g>
          ))}
          <path d="M40 160 C60 172 96 176 128 162" stroke="#5a3421" strokeWidth="5" strokeLinecap="round" opacity="0.6" fill="none" />
        </svg>
      </button>
      <div
        className={"bk-fx" + trailCls}
        aria-hidden="true"
        onPointerDown={(e) => {
          const el = e.currentTarget.parentElement as HTMLElement | null;
          tapFrom(e.clientX, e.clientY, el);
        }}
      >
        {/* Gate ripples/crumbs to onTap only — idle must show no ripple. p.ripples/crumbs are only populated on actual clicks. */}
        {!fxIsOff(density) && p.ripples.map((r) => (
          <span key={r.id} className={FX_CLASS_RIPPLE} style={{ left: r.x * 100 + "%", top: r.y * 100 + "%" }} />
        ))}
        {!fxIsOff(density) && p.lunging.length > 0 && autoRipples.map((r) => (
          <span key={r.id} className={FX_CLASS_RIPPLE + " auto"} style={{ left: r.x * 100 + "%", top: r.y * 100 + "%" }} />
        ))}
        {!fxIsOff(density) && p.crumbs.map((c, i) => (
          <span
            key={c.id}
            className={FX_CLASS_CRUMB + (i % 3 === 0 ? " alt" : "")}
            style={{ left: c.x * 100 + "%", top: c.y * 100 + "%", width: c.s, height: c.s, ["--dx" as string]: c.dx + "px", ["--dy" as string]: c.dy + "px" } as CSSProperties}
          />
        ))}
        {p.floaters.map((f) => (
          <span key={f.id} className={FX_CLASS_FLOAT + (f.big ? " big" : "")} style={{ left: f.x * 100 + "%", top: f.y * 100 + "%", color: f.color }}>{f.text}</span>
        ))}
      </div>
      {sneakyState && sneakyState.x >= 0 && sneakyState.y >= 0 && now < sneakyState.endsAt && (
        <button
          type="button"
          className="bk-sneaky"
          style={{ left: sneakyState.x * 100 + "%", top: sneakyState.y * 100 + "%" }}
          aria-label="Sneaky cookie, expires soon"
          onPointerDown={(e) => {
            e.stopPropagation();
            const el = (e.currentTarget.parentElement as HTMLElement | null) ?? (e.currentTarget as unknown as HTMLElement).parentElement as HTMLElement | null;
            const sid = sneakyState?.sneakyId ?? sneakyState?.id ?? "sneaky_cookie";
            let cx = sneakyState?.x ?? 0.5;
            let cy = sneakyState?.y ?? 0.5;
            if (el) {
              const r = el.getBoundingClientRect();
              cx = Math.min(0.92, Math.max(0.08, (e.clientX - r.left) / r.width));
              cy = Math.min(0.92, Math.max(0.08, (e.clientY - r.top) / r.height));
            }
            p.onSneakyClick?.(sid, cx, cy);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="bk-sneaky-glow" aria-hidden="true" />
          <span className="bk-sneaky-cookie" aria-hidden="true">🍪</span>
          <span className="bk-sneaky-sparkle" aria-hidden="true">✨</span>
        </button>
      )}
      {(minis ?? []).filter((m) => m.x >= 0 && m.y >= 0 && now < m.endsAt).map((m) => (
        <button
          key={m.cookieId}
          type="button"
          className="bk-mini"
          style={{ left: m.x * 100 + "%", top: m.y * 100 + "%" }}
          aria-label={"Mini cookie " + m.multiplier + "x, expires soon"}
          onPointerDown={(e) => {
            e.stopPropagation();
            const el = e.currentTarget.parentElement as HTMLElement | null;
            let cx = m.x;
            let cy = m.y;
            if (el) {
              const r = el.getBoundingClientRect();
              cx = Math.min(0.92, Math.max(0.08, (e.clientX - r.left) / r.width));
              cy = Math.min(0.92, Math.max(0.08, (e.clientY - r.top) / r.height));
            }
            p.onMiniClick?.(m.cookieId, cx, cy);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="bk-mini-glow" aria-hidden="true" />
          <span className="bk-mini-cookie" aria-hidden="true">🍪</span>
          <span className="bk-mini-mult" aria-hidden="true">x{m.multiplier}</span>
        </button>
      ))}
      {hiddenCount > 0 && <div className="bk-hidden">+{hiddenCount} more</div>}
      <div className="bk-countblock">
        {goldenActive && <div className="bk-frenzy" role="status">🌟 Golden Frenzy · 5x clicks</div>}
        <div className={"bk-bignum bk-credits-pill" + (bump ? " bump" : "")}>✨ {fmtInt(credits)} Baker Credits</div>
        <div className="bk-credit-hint" title={clickBreakdown}>+{fmtInt(cookiesPerClick)} 🍪 (+{fmtInt(creditsPerClick)} ✨) per click</div>
        <div className="bk-bakery-total">Bakery {fmtInt(shown)} total</div>
      </div>
      {idleCount > 0 && (
        <div className="bk-cloud-wrap" onMouseEnter={() => setCloudOpen(true)} onMouseLeave={() => setCloudOpen(false)}>
          <button type="button" className="bk-cloud" aria-label={idleCount + " offline bakers"} aria-expanded={cloudOpen} aria-haspopup="dialog" onFocus={() => setCloudOpen(true)} onBlur={() => setCloudOpen(false)} onClick={() => setCloudOpen((v) => !v)}>
            <span className="bk-cloud-puffs" aria-hidden="true"><i /><i /><i /></span>
            <span className="bk-cloud-emoji" aria-hidden="true">☁️</span>
            <span className="bk-cloud-count">{idleCount} offline baker{idleCount === 1 ? "" : "s"}</span>
          </button>
          <div className={"bk-cloud-tip" + (cloudOpen ? " open" : "")} role="dialog" aria-label="Offline bakers">
            <div className="bk-cloud-tip-head">☁️ Offline &amp; AFK — {idleCount}</div>
            <div className="bk-cloud-tip-sub">Idle &lt;5 min or contributors away from cookie</div>
            <div className="bk-cloud-list">
              {sortedIdle.map((pl) => {
                const last = lastSeenMs(pl);
                const glow = glowShadow(pl.cosmetics?.glow);
                return (
                  <div key={pl.userId} className="bk-cloud-row" title={`${pl.name} · 🍪 ${fmtInt(toNum(pl.contribution ?? "0"))} total · +${fmtCompact(toNum(pl.cps ?? "0"))}/s · ${specLabel(pl.spec)} · ${fmtAgo(last, now)}`}>
                    <CloudAvatar pl={pl} />
                    <span className="bk-cloud-name" style={glow ? { textShadow: glow } : undefined}>{pl.name}</span>
                    <span className="bk-cloud-spec">{specLabel(pl.spec)}</span>
                    <span className="bk-cloud-meta">{fmtAgo(last, now)}</span>
                    <span className="bk-cloud-contrib">🍪 {fmtCompact(toNum(pl.contribution ?? "0"))}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
